import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Mail, Trash2, Receipt, Bell, UserCheck, ArrowUpDown, Copy, Send, ClipboardPaste, TestTube2, ArrowRightLeft } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import BulkPasteModal from "../../components/BulkPasteModal";
import ColumnOrderMenu from "../../components/ColumnOrderMenu";
import StudentProfileDrawer from "../../components/StudentProfileDrawer";
import useColumnPrefs from "../../lib/useColumnPrefs";

const SESSIONS = {
  tue_am: "Tuesday AM (10–1)",
  tue_pm: "Tuesday PM (2–5)",
  fri_eve: "Friday Evening (6–8:30)",
};

import { useAuth } from "../../lib/auth";

// Column registry for the CURRENT-term Students table. The order listed here
// is the default. Admins can reorder / hide via the "Columns" menu — their
// preferences persist per-admin in Mongo (see useColumnPrefs).
// "locked" columns are always shown and always stay in their default slot.
const CURRENT_COLUMNS_META = [
  { id: "name", label: "Name", locked: true },
  { id: "contact", label: "Contact" },
  { id: "session", label: "Session" },
  { id: "term", label: "Term" },
  { id: "type", label: "Type (new/returner)" },
  { id: "discount", label: "Discount" },
  { id: "payment", label: "Payment status" },
  { id: "actions", label: "Quick actions" },
  { id: "delete", label: "Delete", locked: true },
];

export default function Students() {
  const [students, setStudents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState("current");
  const [termFilter, setTermFilter] = useState("all");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // all | new | returner
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [copying, setCopying] = useState(null); // student to copy to current term
  const [moving, setMoving] = useState(null); // {student}|null — move to a different term
  const [pasting, setPasting] = useState(false);
  const [terms, setTerms] = useState([]);
  const [selected, setSelected] = useState(() => new Set()); // bulk-move selection on Past tab
  const [profileId, setProfileId] = useState(null); // open student-profile drawer when set
  const [colPrefs, setColPrefs] = useColumnPrefs("students", { order: [], hidden: [] });
  // Extra filters (added alongside the existing term/session/type filters
  // for feature parity with the Leads page). Payment status maps 1-to-1
  // to student.payment_status; starter-pack is derived server-side.
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [starterFilter, setStarterFilter] = useState("all");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: dashboard tiles link here with ?term=Autumn%202026 —
  // apply that filter on first load, then strip the param so subsequent
  // filter changes stay reflected in the URL only if we want them to.
  useEffect(() => {
    const t = searchParams.get("term");
    if (t) {
      setTermFilter(t);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // The "current" term comes from settings — single source of truth.
  const CURRENT_TERM = settings?.current_term || "Summer 2026";

  const load = useCallback(async () => {
    // Each fetch is guarded — staff users don't have the "settings"
    // permission and would otherwise 403 on /admin/settings, which used to
    // reject the whole Promise.all and leave the page blank. We fall back
    // to /admin/settings/public which is available to any authenticated
    // admin and returns just the non-sensitive fields (current_term,
    // session_times, next-term dates) the Students page actually needs.
    const [s, l, st, stPub, tr] = await Promise.all([
      api.get("/admin/students").catch(() => ({ data: [] })),
      api.get("/admin/leads").catch(() => ({ data: [] })),
      api.get("/admin/settings").catch(() => null),
      api.get("/admin/settings/public").catch(() => null),
      api.get("/admin/terms").catch(() => ({ data: [] })),
    ]);
    setStudents(s.data);
    setLeads(l.data);
    setSettings((st && st.data) || (stPub && stPub.data) || {});
    setTerms(tr.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Move a single student to a different term.
  const moveToTerm = async (studentId, targetTerm) => {
    if (!targetTerm) return;
    try {
      await api.patch(`/admin/students/${studentId}`, { term: targetTerm });
      toast.success(`Moved to ${targetTerm} ✓`);
      setMoving(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  // Bulk move N students at once (used on the Past tab toolbar).
  const bulkMove = async (targetTerm, alsoSendWelcome = false) => {
    const ids = Array.from(selected);
    if (!ids.length || !targetTerm) return;
    try {
      const { data } = await api.post("/admin/students/bulk-move-term", { ids, target_term: targetTerm, reset_welcome: alsoSendWelcome });
      toast.success(`Moved ${data.moved} student${data.moved === 1 ? "" : "s"} to ${targetTerm} ✓`);
      if (alsoSendWelcome) {
        // Fire per-student welcome pack sends sequentially — the per-session
        // curriculum auto-picker handles attaching the right PDF for each.
        let sent = 0, failed = 0;
        for (const sid of ids) {
          try { await api.post(`/admin/students/${sid}/send-welcome`, {}); sent++; }
          catch { failed++; }
        }
        toast.success(`Welcome packs: sent ${sent}${failed ? ` · ${failed} skipped` : ""} ✓`);
      }
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk move failed"); }
  };
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // "Active" = students in the currently-active term OR any future term
  // (start_date >= today). Everyone else falls into "Past".
  // This means Autumn 2026 shows up on the "Active/Current" tab as soon as
  // an admin creates the term row — no more surprise "where did my Autumn
  // students go?" moments.
  const activeTermLabels = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const labels = new Set();
    for (const t of terms) {
      if (!t.label) continue;
      // Include if: (a) marked active, (b) has a start_date in the future,
      // or (c) is a terms-collection row with no start_date set — an admin
      // has explicitly created a term row, so treat it as upcoming rather
      // than dropping it silently.
      if (t.is_active) labels.add(t.label);
      else if (!t.start_date) labels.add(t.label);
      else if (t.start_date >= todayIso) labels.add(t.label);
    }
    if (CURRENT_TERM) labels.add(CURRENT_TERM);
    return labels;
  }, [terms, CURRENT_TERM]);
  const currentStudents = useMemo(() => students.filter((s) => activeTermLabels.has(s.term)), [students, activeTermLabels]);
  const pastStudents = useMemo(() => students.filter((s) => !activeTermLabels.has(s.term)), [students, activeTermLabels]);
  const pastTerms = useMemo(() => Array.from(new Set(pastStudents.map((s) => s.term || "—"))).sort().reverse(), [pastStudents]);
  // Sub-filter for the Active tab so an admin can drill into a single
  // future term (e.g. "just show me Autumn 2026 enrolments").
  const activeTerms = useMemo(() => Array.from(activeTermLabels).sort(), [activeTermLabels]);

  const rows = useMemo(() => {
    const filtered = (tab === "current" ? currentStudents : pastStudents).filter((s) => {
      // Term filter applies to BOTH tabs — makes it easy to drill down into
      // e.g. "Autumn 2026" while the Active tab is open, or a specific past
      // term while on Past.
      if (termFilter !== "all" && s.term !== termFilter) return false;
      if (sessionFilter !== "all" && s.session_key !== sessionFilter) return false;
      if (typeFilter !== "all") {
        const t = s.student_type || "new";
        if (typeFilter === "new" && t !== "new") return false;
        if (typeFilter === "returner" && t !== "returner") return false;
      }
      if (paymentFilter !== "all" && (s.payment_status || "unpaid") !== paymentFilter) return false;
      if (starterFilter === "yes" && !s.has_starter_pack) return false;
      if (starterFilter === "no" && s.has_starter_pack) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(s.name || "").toLowerCase().includes(q)
          && !(s.email || "").toLowerCase().includes(q)
          && !(s.phone || "").includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const get = (s) => {
      switch (sortBy) {
        case "name": return (s.name || "").toLowerCase();
        case "email": return (s.email || "").toLowerCase();
        case "session_key": return s.session_key || "";
        case "term": return s.term || "";
        case "payment_status": return s.payment_status || "";
        case "amount_paid": return Number(s.amount_paid || 0);
        default: return s.created_at || "";
      }
    };
    return [...filtered].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [tab, currentStudents, pastStudents, termFilter, sessionFilter, typeFilter, paymentFilter, starterFilter, search, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };

  const save = async (data) => {
    try {
      await api.post("/admin/students", { ...data, term: data.term || CURRENT_TERM });
      toast.success("Student added");
      setShowForm(false);
      await load();
    } catch { toast.error("Failed"); }
  };

  const sendWelcome = async (id, testMode = false) => {
    try {
      const body = testMode ? { test_to: user?.email } : {};
      await api.post(`/admin/students/${id}/send-welcome`, body);
      toast.success(testMode ? `Test sent to ${user?.email} ✓` : "Welcome pack sent ✓");
      if (!testMode) load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendReminder = async (id, testMode = false) => {
    try {
      const body = testMode ? { test_to: user?.email } : {};
      await api.post(`/admin/students/${id}/send-reminder`, body);
      toast.success(testMode ? `Test sent to ${user?.email} ✓` : "Reminder sent ✓");
      if (!testMode) load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setType = async (id, student_type) => {
    await api.patch(`/admin/students/${id}`, { student_type });
    toast.success(student_type === "returner" ? "Marked as returner (£30 discount auto-applied on next invoice)" : "Marked as new student");
    load();
  };

  const setDiscount = async (id, discount) => {
    await api.patch(`/admin/students/${id}`, { discount: Number(discount) || 0 });
    load();
  };

  const setSession = async (id, session_key) => {
    try { await api.patch(`/admin/students/${id}`, { session_key }); toast.success("Session updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const createInvoice = async (id) => {
    try { const { data } = await api.post(`/admin/students/${id}/create-invoice`, {}); toast.success(`Invoice ${data.invoice_number} created (£${data.amount})`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete student?")) return;
    await api.delete(`/admin/students/${id}`); await load();
  };

  const submitCopy = async (form) => {
    if (!copying) return;
    try {
      const { data } = await api.post(`/admin/students/${copying.id}/copy-to-current`, form);
      if (!data.created) {
        toast.info(data.message || "Already enrolled in that term");
      } else {
        const inv = data.invoice;
        toast.success(inv ? `Copied · invoice ${inv.invoice_number} created` : "Copied to current term");
      }
      setCopying(null);
      setTab("current");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const goBulkEmailPast = () => {
    // Bulk email page reads ?audience=past from query string
    navigate("/admin/bulk-email?audience=past");
  };

  const submitBulkPaste = async (body) => {
    const { data } = await api.post("/admin/students/bulk-paste", body);
    await load();
    return data;
  };

  return (
    <div data-testid="admin-students-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">enrolled & welcomed</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Students</h1>
          <p className="text-sm text-[#6E6074] mt-1">
            <strong>Current</strong> = {CURRENT_TERM} (Tuesdays 28 Apr – 28 Jul 26 + Fridays 1 May – 31 Jul 26). Everyone else is in Past.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPasting(true)} className="sac-btn-secondary" data-testid="students-paste-btn"><ClipboardPaste size={16} /> Paste from spreadsheet</button>
          <button onClick={() => setShowForm(true)} className="sac-btn-primary" data-testid="students-add-btn"><Plus size={16} /> Enrol student</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5 border-b border-[#E5E0D8]">
        <TabBtn active={tab === "current"} onClick={() => { setTab("current"); setTermFilter("all"); }} count={currentStudents.length} label={activeTerms.length > 1 ? `Active (${activeTerms.length} terms)` : `Active (${CURRENT_TERM})`} testid="tab-current" />
        <TabBtn active={tab === "past"} onClick={() => { setTab("past"); setTermFilter("all"); }} count={pastStudents.length} label="Past students" testid="tab-past" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={termFilter} onChange={(e) => setTermFilter(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="students-term-filter">
          <option value="all">{tab === "current" ? "All active + upcoming" : "All past terms"}</option>
          {(tab === "current" ? activeTerms : pastTerms).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="students-session-filter">
          <option value="all">All sessions</option>
          {Object.entries(SESSIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="students-type-filter">
          <option value="all">New + Returning</option>
          <option value="new">New only</option>
          <option value="returner">Returning only</option>
        </select>
        <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="students-payment-filter">
          <option value="all">Any payment status</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
          <option value="unpaid">Unpaid</option>
        </select>
        <select value={starterFilter} onChange={(e) => setStarterFilter(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="students-starter-filter">
          <option value="all">Any starter pack</option>
          <option value="yes">Ordered starter pack</option>
          <option value="no">No starter pack</option>
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone"
          className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm w-72" data-testid="students-search" />
        {(termFilter !== "all" || sessionFilter !== "all" || typeFilter !== "all" || paymentFilter !== "all" || starterFilter !== "all" || search) && (
          <button
            onClick={() => { setTermFilter("all"); setSessionFilter("all"); setTypeFilter("all"); setPaymentFilter("all"); setStarterFilter("all"); setSearch(""); }}
            className="text-xs text-[#A685BD] hover:underline"
            data-testid="students-clear-filters"
          >Clear filters</button>
        )}
        {tab === "past" && pastStudents.length > 0 && (
          <button onClick={goBulkEmailPast} className="sac-btn-secondary text-sm" data-testid="bulk-email-past-btn">
            <Send size={14} /> Email past students
          </button>
        )}
        {tab === "current" && (
          <ColumnOrderMenu
            columns={CURRENT_COLUMNS_META}
            value={colPrefs}
            onChange={setColPrefs}
            testid="students-columns-menu"
          />
        )}
        <span className="ml-auto text-sm text-[#6E6074]">{rows.length} student{rows.length === 1 ? "" : "s"}</span>
      </div>

      {/* Bulk move toolbar — shown on both tabs when at least one student is ticked. */}
      {selected.size > 0 && (
        <div className="admin-surface px-4 py-3 mb-3 flex flex-wrap items-center gap-3 bg-[#FAF6EE]" data-testid="bulk-move-toolbar">
          <span className="text-sm text-[#5D2C8F] font-medium">{selected.size} selected</span>
          <span className="text-xs text-[#6E6074]">Move to term:</span>
          <BulkMovePicker terms={terms} onMove={bulkMove} />
          <button onClick={() => setSelected(new Set())} className="text-xs text-[#A685BD] hover:underline ml-auto" data-testid="bulk-move-clear">Clear selection</button>
        </div>
      )}

      {/* Mobile card view — phones only (<md = <768px) */}
      <div className="md:hidden space-y-3" data-testid="students-mobile-cards">
        {rows.map((s) => (
          <div key={s.id} className="admin-surface p-4" data-testid={`student-card-${s.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <button
                onClick={() => setProfileId(s.id)}
                className="min-w-0 text-left"
                data-testid={`m-open-profile-${s.id}`}
              >
                <div className="font-medium text-[#5D2C8F] truncate hover:underline">{s.name}</div>
                <div className="text-xs text-[#6E6074] truncate">{s.email}</div>
                {s.phone && <div className="text-xs text-[#6E6074]">{s.phone}</div>}
              </button>
              <button onClick={() => remove(s.id)} className="p-2 -m-2 text-[#A685BD] shrink-0" aria-label="Delete"><Trash2 size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mt-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#8A7C92] mb-1">Session</div>
                <select value={s.session_key} onChange={(e) => setSession(s.id, e.target.value)}
                  className="bg-white border border-[#E5E0D8] rounded w-full px-2 py-1.5 text-xs" data-testid={`m-session-${s.id}`}>
                  {Object.entries(SESSIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#8A7C92] mb-1">Term</div>
                <div className="px-2 py-1.5 text-[#6E6074]">{s.term || "—"}</div>
              </div>
            </div>
            {tab === "current" && (
              <>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="inline-flex rounded-full bg-[#FAF6EE] p-0.5 text-[11px]">
                    <button onClick={() => setType(s.id, "new")}
                      className={`px-2.5 py-1 rounded-full ${s.student_type !== "returner" ? "bg-white text-[#5D2C8F] shadow-sm" : "text-[#6E6074]"}`}>New</button>
                    <button onClick={() => setType(s.id, "returner")}
                      className={`px-2.5 py-1 rounded-full ${s.student_type === "returner" ? "bg-white text-[#5D2C8F] shadow-sm" : "text-[#6E6074]"}`}>
                      <UserCheck size={11} className="inline mr-1" />Returner
                    </button>
                  </div>
                  <span className={`text-[11px] px-2 py-1 rounded-full ${s.payment_status === 'paid' ? 'bg-[#8EC9C7]/25 text-[#5D2C8F]' : 'bg-[#E8C37A]/25 text-[#8F6B1F]'}`}>{s.payment_status}</span>
                </div>
                <div className="mt-2 text-[11px] text-[#6E6074] flex items-center justify-between">
                  <span>Paid £{s.amount_paid} / £{s.amount_due}</span>
                  <label className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-wide">Discount £</span>
                    <input type="number" min="0" step="1" defaultValue={s.discount || (s.student_type === "returner" ? 30 : 0)}
                      onBlur={(e) => setDiscount(s.id, e.target.value)}
                      className="w-14 bg-white border border-[#E5E0D8] rounded px-1.5 py-1" />
                  </label>
                </div>
                <div className="mt-3 pt-3 border-t border-[#E5DCE6] grid grid-cols-3 gap-2">
                  {s.welcome_sent
                    ? <div className="text-[10px] text-[#8EC9C7] flex items-center justify-center">welcome ✓</div>
                    : <button onClick={() => sendWelcome(s.id)} className="bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-1.5 text-xs flex items-center justify-center gap-1"><Mail size={12} /> Welcome</button>}
                  <button onClick={() => createInvoice(s.id)} className="bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-1.5 text-xs flex items-center justify-center gap-1"><Receipt size={12} /> Invoice</button>
                  <button onClick={() => sendReminder(s.id)} className="bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-1.5 text-xs flex items-center justify-center gap-1"><Bell size={12} /> Remind</button>
                </div>
                {/* Mobile: Move-to-term button (was desktop-only before) */}
                <button onClick={() => setMoving(s)} className="mt-2 w-full bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-2 text-xs flex items-center justify-center gap-1" data-testid={`m-move-to-term-${s.id}`}>
                  <ArrowRightLeft size={12} /> Move to another term…
                </button>
              </>
            )}
            {tab === "past" && (
              <div className="mt-3 pt-3 border-t border-[#E5DCE6] space-y-2">
                <button onClick={() => setCopying(s)} className="w-full bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-2 text-xs flex items-center justify-center gap-1" data-testid={`m-copy-to-current-${s.id}`}>
                  <Copy size={12} /> Copy to current term
                </button>
                <button onClick={() => setMoving(s)} className="w-full bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-2 text-xs flex items-center justify-center gap-1" data-testid={`m-move-to-term-past-${s.id}`}>
                  <ArrowRightLeft size={12} /> Move to another term…
                </button>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className="admin-surface p-10 text-center text-[#6E6074] text-sm">No students match.</div>}
      </div>

      {/* Desktop + tablet table view */}
      <div className="hidden md:block admin-surface overflow-hidden">
        <table className="w-full text-sm">
          {tab === "current" ? (
            <CurrentStudentsTableBody
              rows={rows}
              colPrefs={colPrefs}
              sortBy={sortBy}
              sortDir={sortDir}
              toggleSort={toggleSort}
              handlers={{ setSession, setType, setDiscount, sendWelcome, createInvoice, sendReminder, remove, setMoving, selected, toggleSelect, openProfile: setProfileId, navigate }}
              terms={terms}
            />
          ) : (
            <>
              <thead className="bg-[#FAF6EE] text-left text-[#5D2C8F]">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox"
                      checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                      onChange={() => {
                        const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
                        setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
                      }}
                      aria-label="Select all" data-testid="past-select-all"
                    />
                  </th>
                  <th className="px-3 py-3"><StudentSortBtn label="Name" k="name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} /></th>
                  <th className="px-3 py-3"><StudentSortBtn label="Contact" k="email" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} /></th>
                  <th className="px-3 py-3"><StudentSortBtn label="Session" k="session_key" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} /></th>
                  <th className="px-3 py-3"><StudentSortBtn label="Term" k="term" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} /></th>
                  <th className="px-3 py-3">Actions</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => setProfileId(s.id)}
                    className={`border-t border-[#E5E0D8] align-top cursor-pointer hover:bg-[#FAF6EE]/70 transition-colors ${selected.has(s.id) ? "bg-[#FAF6EE]" : ""}`}
                    data-testid={`past-row-${s.id}`}
                  >
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} aria-label={`Select ${s.name}`} data-testid={`past-select-${s.id}`} />
                    </td>
                    <td className="px-3 py-3 font-medium text-[#5D2C8F] hover:underline">{s.name}</td>
                    <td className="px-3 py-3 text-[#6E6074] text-xs"><div>{s.email}</div><div>{s.phone}</div></td>
                    <td className="px-3 py-3 text-[#5D2C8F] text-xs" onClick={(e) => e.stopPropagation()}>
                      <select value={s.session_key} onChange={(e) => setSession(s.id, e.target.value)}
                        className="bg-white border border-[#E5E0D8] rounded px-2 py-1 text-xs" data-testid={`session-select-${s.id}`}>
                        {Object.entries(SESSIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-[#6E6074] text-xs">{s.term}</td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => setCopying(s)} className="text-[#5D2C8F] inline-flex items-center gap-1 text-xs hover:underline" data-testid={`copy-to-current-${s.id}`}>
                          <Copy size={11} /> Copy to current
                        </button>
                        <button onClick={() => setMoving(s)} className="text-[#5D2C8F] inline-flex items-center gap-1 text-xs hover:underline" data-testid={`move-to-term-${s.id}`}>
                          <ArrowRightLeft size={11} /> Move to term…
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}><button onClick={() => remove(s.id)} className="p-1.5 text-[#A685BD]"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6E6074]">No students match.</td></tr>}
              </tbody>
            </>
          )}
        </table>
      </div>

      {showForm && <StudentForm leads={leads} currentTerm={CURRENT_TERM} onClose={() => setShowForm(false)} onSave={save} />}
      {copying && <CopyToCurrentModal student={copying} settings={settings} onClose={() => setCopying(null)} onSubmit={submitCopy} />}
      {moving && <MoveToTermModal student={moving} terms={terms} onClose={() => setMoving(null)} onMove={(t) => moveToTerm(moving.id, t)} />}
      {pasting && <BulkPasteModal kind="students" settings={settings} onClose={() => setPasting(false)} onSubmit={submitBulkPaste} />}
      {profileId && <StudentProfileDrawer studentId={profileId} onClose={() => setProfileId(null)} onChanged={load} />}
    </div>
  );
}

function StudentSortBtn({ label, k, sortBy, sortDir, onClick }) {
  const active = sortBy === k;
  return (
    <button onClick={() => onClick(k)} className={`inline-flex items-center gap-1 hover:text-[#A685BD] transition-colors ${active ? "underline decoration-[#A685BD] underline-offset-4" : ""}`} data-testid={`students-sort-${k}`}>
      {label} <ArrowUpDown size={11} className={active ? "opacity-100" : "opacity-30"} />
      {active && <span className="text-[10px] ml-0.5">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );
}

/**
 * Renders the Current-term table using the admin's saved column order/visibility.
 * Columns are defined in CURRENT_COLUMNS_META (module-level) and their cell
 * renderers live in this component's `renderers` map.
 */
function CurrentStudentsTableBody({ rows, colPrefs, sortBy, sortDir, toggleSort, handlers, terms }) {
  const { setSession, setType, setDiscount, sendWelcome, createInvoice, sendReminder, remove, setMoving, selected, toggleSelect, openProfile, navigate } = handlers;
  const hidden = new Set(colPrefs?.hidden || []);
  const rawOrder = (colPrefs?.order && colPrefs.order.length)
    ? colPrefs.order.filter((id) => CURRENT_COLUMNS_META.some((c) => c.id === id))
    : CURRENT_COLUMNS_META.map((c) => c.id);
  for (const c of CURRENT_COLUMNS_META) if (!rawOrder.includes(c.id)) rawOrder.push(c.id);
  // Locked columns stay pinned to the ends. Simpler: force `name` first and `delete` last.
  const ordered = [
    "name",
    ...rawOrder.filter((id) => id !== "name" && id !== "delete"),
    "delete",
  ];
  const visible = ordered.filter((id) => !hidden.has(id));

  const th = (id) => {
    switch (id) {
      case "name": return <StudentSortBtn label="Name" k="name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "contact": return <StudentSortBtn label="Contact" k="email" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "session": return <StudentSortBtn label="Session" k="session_key" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "term": return <StudentSortBtn label="Term" k="term" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "type": return "Type";
      case "discount": return "Discount";
      case "payment": return <StudentSortBtn label="Payment" k="payment_status" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "actions": return "Actions";
      case "delete": return "";
      default: return id;
    }
  };

  const td = (id, s) => {
    switch (id) {
      case "name":
        return (
          <td className="px-3 py-3 font-medium text-[#5D2C8F]">
            <button onClick={() => openProfile?.(s.id)} className="text-left hover:underline" data-testid={`open-profile-${s.id}`}>
              {s.name}
            </button>
            {s.has_starter_pack && (
              <span
                className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#E8C37A]/25 text-[#8F6B1F] whitespace-nowrap"
                title="This student has ordered a starter/kit pack (derived from their invoice line items)"
                data-testid={`starter-pack-pill-${s.id}`}
              >Kit</span>
            )}
          </td>
        );
      case "contact":
        return <td className="px-3 py-3 text-[#6E6074] text-xs"><div>{s.email}</div><div>{s.phone}</div></td>;
      case "session":
        return (
          <td className="px-3 py-3 text-[#5D2C8F] text-xs">
            <select value={s.session_key} onChange={(e) => setSession(s.id, e.target.value)}
              className="bg-white border border-[#E5E0D8] rounded px-2 py-1 text-xs" data-testid={`session-select-${s.id}`}>
              {Object.entries(SESSIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </td>
        );
      case "term":
        return <td className="px-3 py-3 text-[#6E6074] text-xs">{s.term}</td>;
      case "type":
        return (
          <td className="px-3 py-3">
            <div className="inline-flex rounded-full bg-[#FAF6EE] p-0.5 text-[10px]">
              <button onClick={() => setType(s.id, "new")} data-testid={`type-new-${s.id}`}
                className={`px-2 py-1 rounded-full ${s.student_type !== "returner" ? "bg-white text-[#5D2C8F] shadow-sm" : "text-[#6E6074]"}`}>New</button>
              <button onClick={() => setType(s.id, "returner")} data-testid={`type-returner-${s.id}`}
                className={`px-2 py-1 rounded-full ${s.student_type === "returner" ? "bg-white text-[#5D2C8F] shadow-sm" : "text-[#6E6074]"}`}>
                <UserCheck size={10} className="inline mr-1" />Returner
              </button>
            </div>
          </td>
        );
      case "discount":
        return (
          <td className="px-3 py-3">
            <div className="flex items-center gap-1">
              <span className="text-xs text-[#6E6074]">£</span>
              <input type="number" min="0" step="1" defaultValue={s.discount || (s.student_type === "returner" ? 30 : 0)}
                onBlur={(e) => setDiscount(s.id, e.target.value)}
                className="w-14 bg-white border border-[#E5E0D8] rounded px-1.5 py-1 text-xs"
                data-testid={`discount-${s.id}`} />
            </div>
          </td>
        );
      case "payment":
        return (
          <td className="px-3 py-3">
            <button
              type="button"
              onClick={() => navigate(`/admin/invoices?student=${s.id}`)}
              className="text-left hover:opacity-80"
              title="Open this student's invoices"
              data-testid={`open-invoices-${s.id}`}
            >
              <span className={`text-xs px-2 py-1 rounded-full ${s.payment_status === 'paid' ? 'bg-[#8EC9C7]/25 text-[#5D2C8F]' : 'bg-[#E8C37A]/25 text-[#8F6B1F]'}`}>{s.payment_status}</span>
              <div className="text-[10px] text-[#6E6074] mt-1 underline underline-offset-2">£{s.amount_paid} / £{s.amount_due}</div>
            </button>
          </td>
        );
      case "actions":
        return (
          <td className="px-3 py-3">
            <div className="flex flex-col gap-1">
              {s.welcome_sent
                ? <span className="text-[10px] text-[#8EC9C7]">welcome sent ✓</span>
                : (
                  <div className="flex items-center gap-2">
                    <button onClick={() => sendWelcome(s.id)} className="text-[#5D2C8F] inline-flex items-center gap-1 text-xs" data-testid={`send-welcome-${s.id}`}><Mail size={11} /> Welcome</button>
                    <button onClick={() => sendWelcome(s.id, true)} title="Send a test copy to your inbox first" className="text-[#A685BD] text-[10px] inline-flex items-center gap-0.5" data-testid={`send-welcome-test-${s.id}`}><TestTube2 size={10} /> test</button>
                  </div>
                )}
              <button onClick={() => createInvoice(s.id)} className="text-[#5D2C8F] inline-flex items-center gap-1 text-xs text-left" data-testid={`create-invoice-${s.id}`}><Receipt size={11} /> Invoice</button>
              <div className="flex items-center gap-2">
                <button onClick={() => sendReminder(s.id)} className="text-[#5D2C8F] inline-flex items-center gap-1 text-xs" data-testid={`send-reminder-${s.id}`}><Bell size={11} /> Reminder</button>
                <button onClick={() => sendReminder(s.id, true)} title="Send a test reminder to your inbox first" className="text-[#A685BD] text-[10px] inline-flex items-center gap-0.5" data-testid={`send-reminder-test-${s.id}`}><TestTube2 size={10} /> test</button>
              </div>
              <button onClick={() => setMoving(s)} className="text-[#5D2C8F] inline-flex items-center gap-1 text-xs text-left" data-testid={`move-current-${s.id}`}><ArrowRightLeft size={11} /> Move to term…</button>
            </div>
          </td>
        );
      case "delete":
        return <td className="px-3 py-3 text-right"><button onClick={() => remove(s.id)} className="p-1.5 text-[#A685BD]"><Trash2 size={14} /></button></td>;
      default:
        return <td />;
    }
  };

  return (
    <>
      <thead className="bg-[#FAF6EE] text-left text-[#5D2C8F]">
        <tr>
          {visible.map((id) => <th key={id} className="px-3 py-3" data-testid={`students-col-${id}`}>{th(id)}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.id} className="border-t border-[#E5E0D8] align-top">
            {visible.map((id) => <React.Fragment key={id}>{td(id, s)}</React.Fragment>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={visible.length} className="px-4 py-10 text-center text-[#6E6074]">No students match.</td></tr>}
      </tbody>
    </>
  );
}

function CopyToCurrentModal({ student, settings, onClose, onSubmit }) {
  const [term, setTerm] = useState(settings?.current_term || "Summer 2026");
  const [sessionKey, setSessionKey] = useState(student.session_key || "tue_am");
  const [createInvoice, setCreateInvoice] = useState(true);
  const [includeStarter, setIncludeStarter] = useState(false);
  const termOptions = useMemo(() => {
    const arr = (settings?.terms || []).map((t) => t.term).filter(Boolean);
    if (settings?.current_term && !arr.includes(settings.current_term)) arr.unshift(settings.current_term);
    return arr.length ? arr : [term];
  }, [settings, term]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="admin-surface w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="copy-to-current-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-1 flex items-center gap-2"><Copy size={18} /> Re-enrol {student.name?.split(" ")[0]}</h3>
        <p className="text-sm text-[#6E6074] mb-4">Adds <strong>{student.name}</strong> as a returner in the new term. Original record stays in Past for history.</p>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-[#5D2C8F]">Term</label>
            <select value={term} onChange={(e) => setTerm(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="copy-term">
              {termOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">Session</label>
            <select value={sessionKey} onChange={(e) => setSessionKey(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="copy-session">
              {Object.entries(SESSIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#5D2C8F] pt-2">
            <input type="checkbox" checked={createInvoice} onChange={(e) => setCreateInvoice(e.target.checked)} data-testid="copy-create-invoice" />
            Auto-create draft invoice (Returner discount applied)
          </label>
          {createInvoice && (
            <label className="flex items-center gap-2 text-sm text-[#5D2C8F] pl-6">
              <input type="checkbox" checked={includeStarter} onChange={(e) => setIncludeStarter(e.target.checked)} data-testid="copy-starter-pack" />
              Include Starter pack add-on
            </label>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="copy-cancel">Cancel</button>
          <button onClick={() => onSubmit({ term, session_key: sessionKey, create_invoice: createInvoice, include_starter_pack: includeStarter })} className="sac-btn-primary" data-testid="copy-submit">Copy to {term}</button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition-colors ${active ? "border-[#5D2C8F] text-[#5D2C8F] font-medium" : "border-transparent text-[#6E6074] hover:text-[#5D2C8F]"}`}>
      {label} <span className="ml-1 text-xs opacity-70">({count})</span>
    </button>
  );
}

function StudentForm({ leads, currentTerm, onClose, onSave }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", session_key: "tue_am", lead_id: "", amount_due: 405, term: currentTerm });
  const onLead = (id) => {
    const l = leads.find((x) => x.id === id);
    if (l) setF({ ...f, lead_id: id, name: l.name, email: l.email || "", phone: l.phone || "" });
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="admin-surface w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="student-form-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-4">Enrol a student</h3>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-[#5D2C8F]">From a lead (optional)</label>
            <select value={f.lead_id} onChange={(e) => onLead(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="studentform-lead">
              <option value="">— Choose a lead —</option>
              {leads.filter((l) => l.status !== 'student' && l.status !== 'closed').map((l) => <option key={l.id} value={l.id}>{l.name} {l.email ? `· ${l.email}` : ''}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <In label="Name*" v={f.name} on={(v) => setF({ ...f, name: v })} testid="studentform-name" />
            <In label="Email" v={f.email} on={(v) => setF({ ...f, email: v })} testid="studentform-email" />
            <In label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} testid="studentform-phone" />
            <div>
              <label className="text-sm text-[#5D2C8F]">Session*</label>
              <select value={f.session_key} onChange={(e) => setF({ ...f, session_key: e.target.value })} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="studentform-session">
                {Object.entries(SESSIONS).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
              </select>
            </div>
            <In label="Term" v={f.term} on={(v) => setF({ ...f, term: v })} testid="studentform-term" />
            <In label="Amount due (£)" type="number" v={f.amount_due} on={(v) => setF({ ...f, amount_due: parseFloat(v || 0) })} testid="studentform-amount" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]">Cancel</button>
          <button onClick={() => onSave(f)} className="sac-btn-primary" data-testid="studentform-save-btn">Save</button>
        </div>
      </div>
    </div>
  );
}

function In({ label, v, on, type = "text", testid }) {
  return <div><label className="text-sm text-[#5D2C8F]">{label}</label><input type={type} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid={testid} /></div>;
}

/**
 * Inline picker used inside the bulk-move toolbar. Lets the admin choose a
 * target term AND optionally fire a welcome-pack send for every moved
 * student — the per-session curriculum auto-picker then attaches the right
 * PDF per recipient.
 */
function BulkMovePicker({ terms, onMove }) {
  const [target, setTarget] = useState("");
  const [alsoSend, setAlsoSend] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={target} onChange={(e) => setTarget(e.target.value)} className="bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-sm" data-testid="bulk-move-term-select">
        <option value="">Pick a term…</option>
        {terms.map((t) => <option key={t.id} value={t.label}>{t.label}{t.is_active ? " (active)" : ""}</option>)}
      </select>
      <label className="text-xs text-[#5D2C8F] inline-flex items-center gap-1.5" data-testid="bulk-move-send-welcome-row">
        <input type="checkbox" checked={alsoSend} onChange={(e) => setAlsoSend(e.target.checked)} data-testid="bulk-move-send-welcome" />
        Send welcome packs after moving (auto-picks the right curriculum)
      </label>
      <button onClick={() => target && onMove(target, alsoSend)} disabled={!target} className="sac-btn-primary !py-1.5 !px-3 text-xs disabled:opacity-50" data-testid="bulk-move-confirm">Move</button>
    </div>
  );
}

/**
 * Modal for picking which term to move a student into. Used both from the
 * Current tab (typically forward-moving a student to a future term they've
 * booked) and from the Past tab (e.g. moving someone back to the active
 * term after they couldn't attend a previous one).
 */
function MoveToTermModal({ student, terms, onClose, onMove }) {
  const [target, setTarget] = useState("");
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="admin-surface w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="move-to-term-modal">
        <h2 className="font-display text-xl text-[#5D2C8F] mb-1">Move {student.name}</h2>
        <p className="text-xs text-[#6E6074] mb-4">Currently in <strong>{student.term || "no term"}</strong>. Choose where to move them — invoices and welcome emails will use the new term automatically.</p>
        {terms.length === 0 ? (
          <div className="text-sm text-[#A685BD]">No terms defined yet. Go to <strong>Settings → Terms</strong> to add one first.</div>
        ) : (
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="move-to-term-select">
            <option value="">Pick a term…</option>
            {terms.filter((t) => t.label !== student.term).map((t) => (
              <option key={t.id} value={t.label}>{t.label}{t.is_active ? " (active)" : ""}</option>
            ))}
          </select>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm text-[#A685BD] px-3 py-2" data-testid="move-to-term-cancel">Cancel</button>
          <button onClick={() => onMove(target)} disabled={!target} className="sac-btn-primary text-sm disabled:opacity-50" data-testid="move-to-term-confirm"><ArrowRightLeft size={14} /> Move student</button>
        </div>
      </div>
    </div>
  );
}
